
	import { Prop, Vue, Watch, Component } from "vue-property-decorator";
	import { CustomFieldDto, CustomFieldTypes, CustomFieldTableTypes } from "@/store/custom-fields/types";
	import { CustomFieldTypesForDisplay, UserFacingCustomFieldType } from "@/types/sv-data/custom-fields/Constants";
	import { numberFormattingMixins } from "@/mixins"
	import api from "@/services/api.customField.service";
	import vSelect3 from "vselect3";

	const { enforceWholeNumber } = numberFormattingMixins.methods;

	@Component({
		components: {
			"v-select-3": vSelect3
		}
	})
	export default class CustomFieldUpdateView extends Vue {
		@Prop({ required: true }) public customFieldTableTypeId: CustomFieldTableTypes;
		@Prop({ required: true }) public actionTitle: string;
		@Prop({ required: true }) public selectedCustomField: CustomFieldDto;
		@Prop({ required: true }) public customFieldToUpdate: CustomFieldDto;
		@Prop({ required: true }) public isUpdateViewShown: boolean;

		private readonly maxLength = 100;
		private readonly minLength = 1;
		private readonly externalIdMaxLength = 200;
		private readonly maxValue = 2147483647;
		private readonly minValue = 0;
		private readonly addActionTitle = "Add";
		private readonly editActionTitle = "Edit";
		private readonly customFieldsWithoutPlaceholder = [CustomFieldTypes.Checkbox, CustomFieldTypes.Date, CustomFieldTypes.Icon];
		private isFocused = false;

		private get customFieldValid(): boolean {
			const externalIdLengthIsValid = this.selectedCustomField.externalIds === undefined ||
				this.selectedCustomField.externalIds === null ||
				JSON.stringify(this.selectedCustomField.externalIds)?.length <= this.externalIdMaxLength;

			return this.selectedCustomField.title != null &&
				this.selectedCustomField.title.length >= this.minLength &&
				this.selectedCustomField.title.length <= this.maxLength &&
				this.selectedCustomField.customFieldTypeId != null &&
				(!(this.selectedCustomField?.placeholder) || this.selectedCustomField.placeholder.length <= this.maxLength) &&
				this.selectedCustomField.order >= 0 &&
				(this.selectedCustomField.customFieldTypeId != CustomFieldTypes.List || !this.isCustomFieldOptionsEmpty) &&
				externalIdLengthIsValid;
		}

		private get hasChanged(): boolean {
			return (this.selectedCustomField != null) &&
				this.selectedCustomField.title != this.customFieldToUpdate.title ||
				this.selectedCustomField.customFieldTypeId != this.customFieldToUpdate.customFieldTypeId ||
				this.selectedCustomField.required != this.customFieldToUpdate.required ||
				this.selectedCustomField.options?.allowMultiple != this.customFieldToUpdate.options?.allowMultiple ||
				this.selectedCustomField.placeholder != this.customFieldToUpdate.placeholder ||
				this.selectedCustomField.order !== this.customFieldToUpdate.order ||
				JSON.stringify(this.selectedCustomField.options?.data) !== JSON.stringify(this.customFieldToUpdate.options?.data) ||
				JSON.stringify(this.selectedCustomField.externalIds) !== JSON.stringify(this.customFieldToUpdate.externalIds);
		}

		private async createCustomField(): Promise<void> {
			try {
				this.selectedCustomField.customFieldTableTypeId = this.customFieldTableTypeId;
				if (this.selectedCustomField.order.toString() == "") {
					this.selectedCustomField.order = null;
				}
				await api.createCustomField(this.selectedCustomField);
				this.showTableView();
			} catch (ex) {
				console.error("Failed to add custom field: " + ex);
			}
		}

		private async updateCustomField(): Promise<void> {
			try {
				await api.updateCustomField(this.selectedCustomField);
				this.showTableView();
			} catch (ex) {
				console.error("Failed to update custom field: " + ex);
			}
		}

		private customFieldOrderFormatter(orderValue: number): number {
			return enforceWholeNumber(orderValue);
		}

		private get showCustomFieldTypeOptions(): UserFacingCustomFieldType[] {
			if (this.customFieldTableTypeId === CustomFieldTableTypes.NotificationTemplate) {
				return CustomFieldTypesForDisplay.filter(type =>
					type.customFieldTypeId === CustomFieldTypes.Text ||
					type.customFieldTypeId === CustomFieldTypes.TextArea ||
					type.customFieldTypeId === CustomFieldTypes.Number ||
					type.customFieldTypeId === CustomFieldTypes.List
				);
			} else {
				return CustomFieldTypesForDisplay.filter(type =>
					type.customFieldTypeId !== CustomFieldTypes.List
				);
			}
		}

		private get customFieldOptionsAllowMultiple(): boolean {
			return this.selectedCustomField.options?.allowMultiple;
		}

		private set customFieldOptionsAllowMultiple(value: boolean) {
			this.selectedCustomField.options.allowMultiple = value;
		}

		private get showCustomFieldOptionsData(): string[] {
			return this.selectedCustomField.options?.data;
		}

		private set showCustomFieldOptionsData(value: string[]) {
			if (!this.selectedCustomField.options) {
				this.selectedCustomField.options = {};
			}

			if (typeof value === "string") {
        		this.selectedCustomField.options.data = [value];
			} else if (value == null) {
				this.selectedCustomField.options.data = [];
			} else {
				this.selectedCustomField.options.data = value;
			}
		}

		private get isCustomFieldOptionsEmpty(): boolean {
			return this.selectedCustomField.options?.data?.length == 0;
		}

		private get externalIdInputValid(): boolean {
			return (this.customFieldTableTypeId === CustomFieldTableTypes.Alarm ||
			this.customFieldTableTypeId === CustomFieldTableTypes.Server ||
			this.customFieldTableTypeId === CustomFieldTableTypes.Device);
		}

		@Watch('selectedCustomField.type')
		private checkRequiredValid(): void {
			if (this.selectedCustomField.customFieldTypeId == CustomFieldTypes.Checkbox) {
				this.selectedCustomField.required = false;
			}
		}

		private showTableView(): void {
			this.$emit("show-table-view");
		}

		private get customFieldPlaceholderState(): boolean {
			return !this.selectedCustomField?.placeholder ||
				this.selectedCustomField.placeholder?.length <= this.maxLength;
		}
	}
